import React, { useState, useEffect, useCallback, useRef } from 'react';
import { marked } from 'marked';
import api from '../utils/api';
import toast from 'react-hot-toast';

marked.setOptions({ breaks: true, gfm: true });

const categoryOptions = [
  { value: 'dasar-pbj', label: 'Dasar PBJ' },
  { value: 'perencanaan', label: 'Perencanaan' },
  { value: 'pemilihan-penyedia', label: 'Pemilihan Penyedia' },
  { value: 'manajemen-kontrak', label: 'Manajemen Kontrak' },
  { value: 'pengadaan-khusus', label: 'Pengadaan Khusus' },
  { value: 'eprocurement', label: 'E-Procurement' },
  { value: 'pengaduan-sanksi', label: 'Pengaduan & Sanksi' },
  { value: 'sertifikasi', label: 'Sertifikasi' }
];

const emptyCourse = { title: '', description: '', category: 'dasar-pbj', difficulty: 'pemula', duration_minutes: 60, is_published: false, thumbnail: '' };
const emptyModule = { title: '', thumbnail_url: '', content: '', content_type: 'text', duration_minutes: 20, order_index: 0, course_id: null };
const emptyQuiz = { question: '', image_url: '', options: ['', '', '', ''], correct_answer: 0, explanation: '', order_index: 0, module_id: null };

// ── Markdown Editor Component ────────────────────────────────────────────────
const MarkdownEditor = ({ value, onChange, minHeight = 300 }) => {
  const [tab, setTab] = useState('edit');
  const textareaRef = useRef(null);

  const insert = (prefix, suffix = '', placeholder = 'teks') => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || placeholder;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const newVal = before + prefix + selected + suffix + after;
    onChange(newVal);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    }, 0);
  };

  const insertLine = (prefix) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newVal = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + prefix.length, start + prefix.length); }, 0);
  };

  const insertImageUrl = () => {
    const url = window.prompt('URL Gambar:');
    if (url) insert(`![gambar](${url})`, '', '');
  };

  const btnStyle = {
    padding: '4px 10px', border: '1px solid var(--border-primary)', borderRadius: 6,
    background: 'var(--bg-tertiary)', color: 'var(--text-secondary)',
    cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, fontFamily: 'var(--font-mono)',
    transition: 'all 0.15s'
  };

  return (
    <div style={{ border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 4, padding: '8px 10px', flexWrap: 'wrap',
        background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-primary)',
        alignItems: 'center'
      }}>
        <button type="button" style={btnStyle} onClick={() => insert('**', '**', 'tebal')} title="Bold">B</button>
        <button type="button" style={{ ...btnStyle, fontStyle: 'italic' }} onClick={() => insert('*', '*', 'miring')} title="Italic">I</button>
        <span style={{ width: 1, height: 20, background: 'var(--border-primary)', margin: '0 4px' }} />
        <button type="button" style={btnStyle} onClick={() => insertLine('# ')} title="H1">H1</button>
        <button type="button" style={btnStyle} onClick={() => insertLine('## ')} title="H2">H2</button>
        <button type="button" style={btnStyle} onClick={() => insertLine('### ')} title="H3">H3</button>
        <span style={{ width: 1, height: 20, background: 'var(--border-primary)', margin: '0 4px' }} />
        <button type="button" style={btnStyle} onClick={() => insertLine('- ')} title="Bullet list">• List</button>
        <button type="button" style={btnStyle} onClick={() => insertLine('1. ')} title="Numbered list">1. List</button>
        <button type="button" style={btnStyle} onClick={() => insert('`', '`', 'kode')} title="Inline code">{'`kode`'}</button>
        <span style={{ width: 1, height: 20, background: 'var(--border-primary)', margin: '0 4px' }} />
        <button type="button" style={btnStyle} onClick={() => insert('[', '](url)', 'teks link')} title="Link">🔗 Link</button>
        <button type="button" style={btnStyle} onClick={insertImageUrl} title="Sisipkan gambar">🖼️ Gambar</button>
        <span style={{ width: 1, height: 20, background: 'var(--border-primary)', margin: '0 4px' }} />
        <button type="button" style={btnStyle} onClick={() => insert('> ', '', 'kutipan')} title="Blockquote">❝ Quote</button>
        <button type="button" style={btnStyle} onClick={() => onChange(value + '\n---\n')} title="Garis pemisah">— HR</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          {['edit', 'preview', 'split'].map(t => (
            <button key={t} type="button"
              style={{ ...btnStyle, background: tab === t ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: tab === t ? 'white' : 'var(--text-secondary)', border: 'none' }}
              onClick={() => setTab(t)}>
              {t === 'edit' ? '✏️ Edit' : t === 'preview' ? '👁 Preview' : '⬛ Split'}
            </button>
          ))}
        </div>
      </div>

      {/* Editor Area */}
      <div style={{ display: 'flex', minHeight }}>
        {(tab === 'edit' || tab === 'split') && (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
              flex: 1, minHeight, padding: '14px 16px', border: 'none', outline: 'none', resize: 'vertical',
              fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', lineHeight: 1.7,
              background: 'var(--bg-card)', color: 'var(--text-primary)',
              borderRight: tab === 'split' ? '1px solid var(--border-primary)' : 'none'
            }}
            placeholder={`Tulis konten modul dengan Markdown...\n\n# Judul Besar\n## Sub Judul\n\nTeks biasa, **tebal**, *miring*\n\n- Item list\n- Item lain\n\n![gambar](https://url-gambar.com/img.jpg)`}
          />
        )}
        {(tab === 'preview' || tab === 'split') && (
          <div
            style={{
              flex: 1, minHeight, padding: '14px 16px', overflowY: 'auto',
              background: 'var(--bg-card)', color: 'var(--text-primary)',
              fontSize: '0.9rem', lineHeight: 1.8
            }}
            className="content-html md-preview"
            dangerouslySetInnerHTML={{ __html: value ? marked(value) : '<p style="color:var(--text-tertiary);font-style:italic">Preview akan muncul di sini...</p>' }}
          />
        )}
      </div>
      <div style={{ padding: '6px 12px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-primary)', fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>
        {value.length} karakter • {value.split('\n').length} baris • Gunakan toolbar atau ketik Markdown langsung
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const AdminCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [courseForm, setCourseForm] = useState(emptyCourse);
  const [editCourseId, setEditCourseId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [moduleForm, setModuleForm] = useState(emptyModule);
  const [editModuleId, setEditModuleId] = useState(null);
  const [deleteModuleConfirm, setDeleteModuleConfirm] = useState(null);

  const [selectedModule, setSelectedModule] = useState(null);
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [quizForm, setQuizForm] = useState(emptyQuiz);
  const [editQuizId, setEditQuizId] = useState(null);
  const [deleteQuizConfirm, setDeleteQuizConfirm] = useState(null);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/learning/courses');
      setCourses(data.courses);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const fetchCourseDetail = async (id) => {
    try {
      const { data } = await api.get(`/learning/courses/${id}`);
      setCourseDetail(data.course);
      setSelectedCourse(id);
    } catch (err) { toast.error('Gagal memuat detail'); }
  };

  // COURSE CRUD
  const openCreateCourse = () => { setCourseForm(emptyCourse); setEditCourseId(null); setShowCourseForm(true); };
  const openEditCourse = (c) => {
    setCourseForm({ title: c.title, description: c.description || '', category: c.category, difficulty: c.difficulty, duration_minutes: c.duration_minutes, is_published: c.is_published, thumbnail: c.thumbnail || '' });
    setEditCourseId(c.id); setShowCourseForm(true);
  };
  const handleSaveCourse = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editCourseId) { await api.put(`/learning/courses/${editCourseId}`, courseForm); toast.success('Kursus diperbarui'); }
      else { await api.post('/learning/courses', courseForm); toast.success('Kursus dibuat'); }
      setShowCourseForm(false); fetchCourses();
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); } finally { setSaving(false); }
  };
  const handleDeleteCourse = async () => {
    try {
      await api.delete(`/learning/courses/${deleteConfirm.id}`);
      toast.success('Kursus dihapus'); setDeleteConfirm(null); fetchCourses();
    } catch (err) { toast.error('Gagal menghapus'); }
  };

  // MODULE CRUD
  const openCreateModule = () => {
    setModuleForm({ ...emptyModule, course_id: selectedCourse, order_index: (courseDetail?.modules?.length || 0) + 1 });
    setEditModuleId(null); setShowModuleForm(true);
  };
  const openEditModule = (mod) => {
    setModuleForm({
      title: mod.title, thumbnail_url: mod.thumbnail_url || '',
      content: mod.content || '', content_type: mod.content_type,
      duration_minutes: mod.duration_minutes, order_index: mod.order_index, course_id: selectedCourse
    });
    setEditModuleId(mod.id); setShowModuleForm(true);
  };
  const handleSaveModule = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editModuleId) { await api.put(`/learning/modules/${editModuleId}`, moduleForm); toast.success('Modul diperbarui'); }
      else { await api.post('/learning/modules', moduleForm); toast.success('Modul dibuat'); }
      setShowModuleForm(false); fetchCourseDetail(selectedCourse);
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); } finally { setSaving(false); }
  };
  const handleDeleteModule = async () => {
    try {
      await api.delete(`/learning/modules/${deleteModuleConfirm.id}`);
      toast.success('Modul dihapus'); setDeleteModuleConfirm(null); fetchCourseDetail(selectedCourse);
    } catch (err) { toast.error('Gagal menghapus'); }
  };

  // QUIZ CRUD
  const openQuizManager = (mod) => { setSelectedModule(mod); };
  const openCreateQuiz = () => {
    setQuizForm({ ...emptyQuiz, module_id: selectedModule.id, order_index: (selectedModule.quizzes?.length || 0) + 1 });
    setEditQuizId(null); setShowQuizForm(true);
  };
  const openEditQuiz = (q) => {
    const opts = Array.isArray(q.options) ? q.options : (typeof q.options === 'string' ? JSON.parse(q.options) : ['', '', '', '']);
    setQuizForm({
      question: q.question, image_url: q.image_url || '',
      options: opts, correct_answer: q.correct_answer,
      explanation: q.explanation || '', order_index: q.order_index, module_id: selectedModule.id
    });
    setEditQuizId(q.id); setShowQuizForm(true);
  };
  const handleSaveQuiz = async (e) => {
    e.preventDefault();
    if (quizForm.options.some(o => !o.trim())) { toast.error('Semua opsi harus diisi'); return; }
    setSaving(true);
    try {
      if (editQuizId) { await api.put(`/learning/quizzes/${editQuizId}`, quizForm); toast.success('Quiz diperbarui'); }
      else { await api.post('/learning/quizzes', quizForm); toast.success('Quiz dibuat'); }
      setShowQuizForm(false);
      const { data } = await api.get(`/learning/courses/${selectedCourse}`);
      setCourseDetail(data.course);
      const updatedMod = data.course.modules.find(m => m.id === selectedModule.id);
      if (updatedMod) setSelectedModule(updatedMod);
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); } finally { setSaving(false); }
  };
  const handleDeleteQuiz = async () => {
    try {
      await api.delete(`/learning/quizzes/${deleteQuizConfirm.id}`);
      toast.success('Quiz dihapus'); setDeleteQuizConfirm(null);
      const { data } = await api.get(`/learning/courses/${selectedCourse}`);
      setCourseDetail(data.course);
      const updatedMod = data.course.modules.find(m => m.id === selectedModule.id);
      if (updatedMod) setSelectedModule(updatedMod);
    } catch (err) { toast.error('Gagal menghapus'); }
  };

  const setC = (key) => (e) => setCourseForm(p => ({ ...p, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setM = (key) => (e) => setModuleForm(p => ({ ...p, [key]: e.target.value }));

  // ── QUIZ MANAGEMENT VIEW ────────────────────────────────────────────────────
  if (selectedCourse && courseDetail && selectedModule) {
    return (
      <div className="fade-in">
        <button className="btn btn-ghost btn-sm" onClick={() => setSelectedModule(null)} style={{ marginBottom: 20 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          Kembali ke Modul
        </button>

        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span className="badge badge-blue" style={{ marginBottom: 6 }}>Quiz</span>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 4 }}>{selectedModule.title}</h2>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{selectedModule.quizzes?.length || 0} pertanyaan</p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={openCreateQuiz}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Tambah Pertanyaan
            </button>
          </div>
        </div>

        {(!selectedModule.quizzes || selectedModule.quizzes.length === 0) ? (
          <div className="empty-state">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>
            <p>Belum ada quiz</p>
            <button className="btn btn-primary btn-sm" onClick={openCreateQuiz}>Buat Quiz Pertama</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedModule.quizzes.map((q, i) => {
              const opts = Array.isArray(q.options) ? q.options : (typeof q.options === 'string' ? JSON.parse(q.options) : []);
              return (
                <div key={q.id} className="card" style={{ padding: '18px 22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-soft)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--accent-primary)', fontWeight: 700, fontSize: '0.75rem', flexShrink: 0, marginTop: 2
                        }}>{i + 1}</div>
                        <div style={{ flex: 1 }}>
                          {q.image_url && (
                            <div style={{ marginBottom: 8 }}>
                              <img src={q.image_url} alt="soal" style={{ maxHeight: 160, maxWidth: '100%', borderRadius: 8, objectFit: 'contain', background: 'var(--bg-tertiary)' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>{q.question}</p>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8, paddingLeft: 38 }}>
                        {opts.map((opt, oi) => (
                          <div key={oi} style={{
                            fontSize: '0.8125rem', padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                            background: oi === q.correct_answer ? 'rgba(34,197,94,0.12)' : 'var(--bg-tertiary)',
                            color: oi === q.correct_answer ? '#16a34a' : 'var(--text-secondary)',
                            fontWeight: oi === q.correct_answer ? 600 : 400,
                            border: oi === q.correct_answer ? '1px solid rgba(34,197,94,0.3)' : '1px solid transparent'
                          }}>
                            {String.fromCharCode(65 + oi)}. {opt}
                          </div>
                        ))}
                      </div>
                      {q.explanation && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', paddingLeft: 38, fontStyle: 'italic' }}>
                          💡 {q.explanation}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditQuiz(q)} title="Edit">
                        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                      </button>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteQuizConfirm(q)} style={{ color: 'var(--error)' }} title="Hapus">
                        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Quiz Form Modal */}
        {showQuizForm && (
          <div className="modal-overlay" onClick={() => setShowQuizForm(false)}>
            <div className="modal" style={{ maxWidth: 620, maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title">{editQuizId ? 'Edit Pertanyaan' : 'Tambah Pertanyaan Baru'}</h2>
              <form onSubmit={handleSaveQuiz}>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Pertanyaan *</label>
                  <textarea className="form-textarea" value={quizForm.question}
                    onChange={e => setQuizForm(p => ({ ...p, question: e.target.value }))}
                    required style={{ minHeight: 80 }} placeholder="Tuliskan pertanyaan quiz..." />
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">URL Gambar Soal <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(opsional)</span></label>
                  <input className="form-input" value={quizForm.image_url}
                    onChange={e => setQuizForm(p => ({ ...p, image_url: e.target.value }))}
                    placeholder="https://contoh.com/gambar-soal.jpg" />
                  {quizForm.image_url && (
                    <div style={{ marginTop: 8, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                      <img src={quizForm.image_url} alt="preview" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Opsi Jawaban * <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(klik huruf untuk pilih jawaban benar)</span></label>
                  {quizForm.options.map((opt, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <label style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                        background: quizForm.correct_answer === i ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'var(--bg-tertiary)',
                        color: quizForm.correct_answer === i ? 'white' : 'var(--text-secondary)',
                        fontWeight: 700, fontSize: '0.8125rem',
                        border: quizForm.correct_answer === i ? 'none' : '1px solid var(--border-primary)',
                        transition: 'all 0.2s'
                      }}>
                        <input type="radio" name="correct" value={i} checked={quizForm.correct_answer === i}
                          onChange={() => setQuizForm(p => ({ ...p, correct_answer: i }))}
                          style={{ display: 'none' }} />
                        {String.fromCharCode(65 + i)}
                      </label>
                      <input className="form-input" value={opt} placeholder={`Opsi ${String.fromCharCode(65 + i)}`}
                        onChange={e => {
                          const newOpts = [...quizForm.options];
                          newOpts[i] = e.target.value;
                          setQuizForm(p => ({ ...p, options: newOpts }));
                        }} required style={{ flex: 1 }} />
                    </div>
                  ))}
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Penjelasan <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(opsional)</span></label>
                  <textarea className="form-textarea" value={quizForm.explanation}
                    onChange={e => setQuizForm(p => ({ ...p, explanation: e.target.value }))}
                    style={{ minHeight: 60 }} placeholder="Jelaskan mengapa jawaban tersebut benar..." />
                </div>
                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="form-label">Urutan</label>
                  <input type="number" className="form-input" value={quizForm.order_index}
                    onChange={e => setQuizForm(p => ({ ...p, order_index: parseInt(e.target.value) || 0 }))}
                    style={{ width: 100 }} />
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowQuizForm(false)}>Batal</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : 'Simpan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Quiz Confirm */}
        {deleteQuizConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteQuizConfirm(null)}>
            <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title" style={{ color: 'var(--error)' }}>Hapus Quiz</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 24 }}>Yakin menghapus pertanyaan ini?</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setDeleteQuizConfirm(null)}>Batal</button>
                <button className="btn btn-danger" onClick={handleDeleteQuiz}>Hapus</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── MODULE MANAGEMENT VIEW ──────────────────────────────────────────────────
  if (selectedCourse && courseDetail) {
    return (
      <div className="fade-in">
        <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedCourse(null); setCourseDetail(null); }} style={{ marginBottom: 20 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          Kembali ke Daftar Kursus
        </button>

        <div className="card" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 4 }}>{courseDetail.title}</h2>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{courseDetail.description}</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Modul ({courseDetail.modules?.length || 0})</h3>
          <button className="btn btn-primary btn-sm" onClick={openCreateModule}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Tambah Modul
          </button>
        </div>

        {courseDetail.modules?.length === 0 ? (
          <div className="empty-state"><p>Belum ada modul</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {courseDetail.modules.map((mod, i) => (
              <div key={mod.id} className="card" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {mod.thumbnail_url ? (
                      <img src={mod.thumbnail_url} alt={mod.title}
                        style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                        onError={e => {
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                        }} />
                    ) : null}
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-soft)', flexShrink: 0,
                      display: mod.thumbnail_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--accent-primary)', fontWeight: 700, fontSize: '0.8125rem'
                    }}>{i + 1}</div>
                    <div>
                      <h4 style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{mod.title}</h4>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                        {mod.content_type} • {mod.duration_minutes} menit
                        {mod.quizzes?.length > 0 && ` • ${mod.quizzes.length} quiz`}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Kelola Quiz" onClick={() => openQuizManager(mod)}
                      style={{ color: mod.quizzes?.length > 0 ? '#22c55e' : 'var(--text-tertiary)' }}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditModule(mod)}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                    </button>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteModuleConfirm(mod)} style={{ color: 'var(--error)' }}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Module Form Modal — Full Screen */}
        {showModuleForm && (
          <div className="modal-overlay" onClick={() => setShowModuleForm(false)}>
            <div className="modal" style={{ maxWidth: 900, width: '95vw', maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title">{editModuleId ? 'Edit Modul' : 'Tambah Modul Baru'}</h2>
              <form onSubmit={handleSaveModule}>
                {/* Thumbnail + Judul */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Judul Modul *</label>
                    <input className="form-input" value={moduleForm.title} onChange={setM('title')} required placeholder="Judul modul..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL Gambar Header <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(opsional)</span></label>
                    <input className="form-input" value={moduleForm.thumbnail_url} onChange={setM('thumbnail_url')} placeholder="https://contoh.com/gambar-header.jpg" />
                  </div>
                </div>

                {/* Thumbnail Preview */}
                {moduleForm.thumbnail_url && (
                  <div style={{ marginBottom: 14, borderRadius: 8, overflow: 'hidden', maxHeight: 160, background: 'var(--bg-tertiary)' }}>
                    <img src={moduleForm.thumbnail_url} alt="preview"
                      style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}

                {/* Tipe + Durasi + Urutan */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Tipe Konten</label>
                    <select className="form-select" value={moduleForm.content_type} onChange={setM('content_type')}>
                      <option value="text">Teks / Markdown</option>
                      <option value="video">Video</option>
                      <option value="quiz">Quiz</option>
                      <option value="file">File</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Durasi (menit)</label>
                    <input type="number" className="form-input" value={moduleForm.duration_minutes} onChange={setM('duration_minutes')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Urutan</label>
                    <input type="number" className="form-input" value={moduleForm.order_index} onChange={setM('order_index')} />
                  </div>
                </div>

                {/* Markdown Editor */}
                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="form-label">Konten Modul <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(Markdown — gunakan toolbar untuk format)</span></label>
                  <MarkdownEditor
                    value={moduleForm.content}
                    onChange={val => setModuleForm(p => ({ ...p, content: val }))}
                    minHeight={320}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModuleForm(false)}>Batal</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : 'Simpan Modul'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Module Confirm */}
        {deleteModuleConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteModuleConfirm(null)}>
            <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title" style={{ color: 'var(--error)' }}>Hapus Modul</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 24 }}>
                Yakin menghapus modul <strong>"{deleteModuleConfirm.title}"</strong>?
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setDeleteModuleConfirm(null)}>Batal</button>
                <button className="btn btn-danger" onClick={handleDeleteModule}>Hapus</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── COURSE LIST ─────────────────────────────────────────────────────────────
  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Kelola Kursus Pembelajaran</h2>
        <button className="btn btn-primary" onClick={openCreateCourse}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Tambah Kursus
        </button>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat...</span></div>
      ) : courses.length === 0 ? (
        <div className="empty-state"><p>Belum ada kursus</p><button className="btn btn-primary btn-sm" onClick={openCreateCourse}>Buat Kursus</button></div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Judul</th><th>Kategori</th><th>Tingkat</th><th>Modul</th><th>Durasi</th><th>Pembuat</th><th>Status</th>
                <th style={{ width: 150 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {courses.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</td>
                  <td><span className="badge badge-orange">{categoryOptions.find(o => o.value === c.category)?.label}</span></td>
                  <td><span className={`badge ${c.difficulty === 'pemula' ? 'badge-green' : c.difficulty === 'menengah' ? 'badge-yellow' : 'badge-red'}`}>{c.difficulty}</span></td>
                  <td>{c.modules?.length || c.total_modules || 0}</td>
                  <td>{c.duration_minutes} mnt</td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{c.creator?.name || '-'}</td>
                  <td><span className={`badge ${c.is_published ? 'badge-green' : 'badge-gray'}`}>{c.is_published ? 'Publik' : 'Draft'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Kelola Modul" onClick={() => fetchCourseDetail(c.id)}>
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" /></svg>
                      </button>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => openEditCourse(c)}>
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                      </button>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Hapus" onClick={() => setDeleteConfirm(c)} style={{ color: 'var(--error)' }}>
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Course Form Modal */}
      {showCourseForm && (
        <div className="modal-overlay" onClick={() => setShowCourseForm(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">{editCourseId ? 'Edit Kursus' : 'Buat Kursus Baru'}</h2>
            <form onSubmit={handleSaveCourse}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Judul *</label>
                <input className="form-input" value={courseForm.title} onChange={setC('title')} required />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Deskripsi</label>
                <textarea className="form-textarea" value={courseForm.description} onChange={setC('description')} />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">URL Gambar Thumbnail <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(opsional)</span></label>
                <input className="form-input" value={courseForm.thumbnail || ''} onChange={setC('thumbnail')} placeholder="https://contoh.com/gambar-thumbnail.jpg" />
                {courseForm.thumbnail && (
                  <div style={{ marginTop: 8, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                    <img src={courseForm.thumbnail} alt="preview" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'cover', borderRadius: 6 }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="form-group">
                  <label className="form-label">Kategori</label>
                  <select className="form-select" value={courseForm.category} onChange={setC('category')}>
                    {categoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Tingkat</label>
                  <select className="form-select" value={courseForm.difficulty} onChange={setC('difficulty')}>
                    <option value="pemula">Pemula</option>
                    <option value="menengah">Menengah</option>
                    <option value="lanjut">Lanjut</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                <div className="form-group">
                  <label className="form-label">Durasi (menit)</label>
                  <input type="number" className="form-input" value={courseForm.duration_minutes} onChange={setC('duration_minutes')} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ marginBottom: 10 }}>Publikasi</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
                    <input type="checkbox" checked={courseForm.is_published} onChange={setC('is_published')} />
                    Publikasikan kursus
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCourseForm(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 className="modal-title" style={{ color: 'var(--error)' }}>Hapus Kursus</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 24 }}>
              Yakin menghapus <strong>"{deleteConfirm.title}"</strong> beserta semua modulnya?
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Batal</button>
              <button className="btn btn-danger" onClick={handleDeleteCourse}>Hapus</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .md-preview h1 { font-size: 1.5rem; font-weight: 700; margin: 20px 0 10px; }
        .md-preview h2 { font-size: 1.25rem; font-weight: 700; margin: 18px 0 9px; }
        .md-preview h3 { font-size: 1.0625rem; font-weight: 600; margin: 14px 0 7px; }
        .md-preview p { margin-bottom: 10px; }
        .md-preview ul, .md-preview ol { padding-left: 22px; margin-bottom: 10px; }
        .md-preview li { margin-bottom: 4px; }
        .md-preview strong { font-weight: 600; }
        .md-preview code { background: var(--bg-tertiary); padding: 2px 6px; border-radius: 4px; font-family: var(--font-mono); font-size: 0.85em; }
        .md-preview pre { background: var(--bg-tertiary); padding: 12px; border-radius: 8px; overflow-x: auto; margin-bottom: 12px; }
        .md-preview img { max-width: 100%; border-radius: 8px; margin: 8px 0; }
        .md-preview blockquote { border-left: 3px solid var(--accent-primary); padding-left: 14px; color: var(--text-secondary); margin: 12px 0; font-style: italic; }
        .md-preview hr { border: none; border-top: 1px solid var(--border-primary); margin: 16px 0; }
        .md-preview table { width: 100%; border-collapse: collapse; margin: 14px 0; }
        .md-preview th, .md-preview td { padding: 8px 12px; border: 1px solid var(--border-primary); text-align: left; font-size: 0.875rem; }
        .md-preview th { background: var(--bg-tertiary); font-weight: 600; }
        .content-html h1 { font-size: 1.5rem; font-weight: 700; margin: 24px 0 12px; color: var(--text-primary); }
        .content-html h2 { font-size: 1.25rem; font-weight: 700; margin: 20px 0 10px; color: var(--text-primary); }
        .content-html h3 { font-size: 1.0625rem; font-weight: 600; margin: 16px 0 8px; color: var(--text-primary); }
        .content-html p { margin-bottom: 12px; }
        .content-html ul, .content-html ol { padding-left: 24px; margin-bottom: 12px; }
        .content-html li { margin-bottom: 6px; }
        .content-html strong { font-weight: 600; color: var(--text-accent); }
        .content-html code { background: var(--bg-tertiary); padding: 2px 7px; border-radius: 4px; font-family: var(--font-mono); font-size: 0.85em; }
        .content-html pre { background: var(--bg-tertiary); padding: 14px; border-radius: 8px; overflow-x: auto; margin-bottom: 14px; }
        .content-html img { max-width: 100%; border-radius: 10px; margin: 10px 0; }
        .content-html blockquote { border-left: 3px solid var(--accent-primary); padding-left: 16px; color: var(--text-secondary); margin: 14px 0; font-style: italic; }
        .content-html hr { border: none; border-top: 1px solid var(--border-primary); margin: 20px 0; }
        .content-html table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        .content-html th, .content-html td { padding: 10px 14px; border: 1px solid var(--border-primary); text-align: left; }
        .content-html th { background: var(--bg-tertiary); font-weight: 600; }
        @media (max-width: 768px) { div[style*="grid-template-columns: 900px"] { width: 95vw !important; } }
      `}</style>
    </div>
  );
};

export default AdminCoursesPage;
